import React, { useEffect, useRef } from 'react'
import { Link } from 'gatsby'
import classNames from 'classnames'
import Swiper from 'swiper'
import { Navigation, Pagination } from 'swiper/modules'
import Badge from '@toasttab/shared/src/components/atoms/Badge/Badge'
import Typography from '@toasttab/shared/src/components/atoms/Typography/Typography'
import SliderNavigation from '@toasttab/shared/src/components/SliderNavigation/SliderNavigation'
import SliderPagination from '@toasttab/shared/src/components/molecules/SliderPagination/SliderPagination'
import { useSiteMetadata } from '@toasttab/onTheLine/src/hooks/use-site-metadata'
import { transformContentfulArticle } from '@toasttab/otl/src/utils/siteUtils'
import { useI18next } from '@toasttab/shared/src/hooks/useI18next'
import { Module } from '@toasttab/otl/src/interfaces/Module.d'
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'

const RelatedArticlesSlider = ({ onTheLineModules }: any) => {
  const { region, t } = useI18next()
  let relatedArticles: any = []
  const releatedArticlesData = onTheLineModules?.filter(
    (m: Module) => m.__typename === 'ContentfulRelatedArticles'
  )
  releatedArticlesData?.forEach((articles: any) => {
    articles.relatedArticles?.forEach((article: any) => {
      relatedArticles.push(transformContentfulArticle(region, article))
    })
  })
  const ref = useRef<any | null>(null)
  const { pathPrefix } = useSiteMetadata()

  useEffect(() => {
    if (ref.current) {
      // init Swiper:
      const swiper = new Swiper(ref.current.querySelector(`.swiper`), {
        // configure Swiper to use modules
        modules: [Navigation, Pagination],
        loop: relatedArticles.length > 3,
        spaceBetween: 24,
        slidesPerView: 1.15,

        // If we need pagination
        pagination: {
          el: ref.current.querySelector(`.swiper-pagination`),
          clickable: true
        },
        // Navigation arrows
        navigation: {
          nextEl: ref.current.querySelector(`.swiper-button-next`),
          prevEl: ref.current.querySelector(`.swiper-button-prev`)
        },
        breakpoints: {
          768: {
            slidesPerView: 2.25
          },
          1024: {
            slidesPerView: 3.25
          }
        }
      })
    }
  }, [])

  const sliderClasses = classNames(
    'swiper !overflow-visible pb-1 relative',
    'after:content-[""] after:block after:absolute after:top-0 after:bottom-0 after:w-full after:z-[2] after:left-full',
    'before:content-[""] before:block before:absolute before:top-0 before:bottom-0 before:w-full before:z-[2] before:right-full',
    'after:bg-transparent after:md:bg-white before:bg-transparent before:md:bg-white',
    'after:bg-slider-fade-left-white before:bg-slider-fade-right-white'
  )
  if (relatedArticles.length) {
    return (
      <section
        className='ds-wrapper mt-8 mb-8 w-screen overflow-hidden'
        ref={ref}
      >
        <div className='ds-container overflow-hidden lg:overflow-visible'>
          <Badge
            badgeColor='bg-yellow-25'
            classes='text-xs mb-4'
            icon='time'
            text='Trending'
            uppercase
          />
          <Typography
            variation='h4'
            as='h4'
            classes='text-default text-3xl font-semibold'
            allowHtml={false}
          >
            {t('Read More')}
          </Typography>

          <div className={sliderClasses}>
            <div className='swiper-wrapper mb-8'>
              {relatedArticles.map((article: any, i: number) => {
                if (article?.__typename) {
                  return (
                    <div
                      className='swiper-slide mb-8 lg:mb-0'
                      key={`relatedArticle-${i}`}
                    >
                      <Link to={`${pathPrefix}/${article.slug}`}>
                        <div className='!w-[312px] !h-[305px] p-6 rounded-lg shadow-lg'>
                          {Boolean(
                            article?.entryImages[0]?.thumbnailImage?.length
                          ) ? (
                            <div className='w-[264px] h-[161px] mb-6'>
                              <picture>
                                <source
                                  srcSet={
                                    article.entryImages[0].thumbnailImage[0]
                                      .optimizedImages.src
                                  }
                                  itemProp='image'
                                  title={
                                    article.entryImages[0].thumbnailImage[0]
                                      .title
                                  }
                                />
                                <source
                                  srcSet={
                                    article.entryImages[0].thumbnailImage[0]
                                      .optimizedImages.srcWebp
                                  }
                                  sizes='100vw'
                                  type='image/webp'
                                />
                                <img
                                  src={
                                    article.entryImages[0].thumbnailImage[0]
                                      .optimizedImages.src
                                  }
                                  alt={
                                    article.entryImages[0].thumbnailImage[0]
                                      .title
                                  }
                                  itemProp='image'
                                  className='lazyload w-full object-cover h-full max-w-full rounded-lg'
                                />
                              </picture>
                            </div>
                          ) : (
                            <div className='w-[264px] h-[161px] mb-6'></div>
                          )}
                          <Typography
                            variation='caption'
                            as='p'
                            classes='text-xs font-calibre uppercase mb-2'
                            allowHtml={false}
                          >
                            {article.onTheLineTopic[0].title}
                          </Typography>

                          <Typography
                            variation='h4'
                            as='h4'
                            classes='text-default text-base !font-semibold'
                            allowHtml={false}
                          >
                            {article.heroTitle}
                          </Typography>
                        </div>
                      </Link>
                    </div>
                  )
                }
              })}
            </div>
            <SliderNavigation
              previousNextButtons='slide-nav-blue-75'
              classes='relative w-[calc(100%_-_16px)] lg:w-full lg:absolute lg:top-0 lg:ml-0 lg:mr-0'
              swiper={null}
            />
            <SliderPagination
              classes='w-[calc(100%_-_16px)] !-z-10 lg:w-full !mt-0 lg:!text-left -top-[15px] lg:-top-0'
              pagination='slide-pag-gray-125'
            />
          </div>
        </div>
      </section>
    )
  }
}

export default RelatedArticlesSlider
